import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";
import { AxiosError, AxiosResponse } from "axios";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { createSearchParams, Link as RouterLink } from "react-router-dom";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import DataGrid from "src/components/common/DataGrid";
import EmptyView from "src/components/common/EmptyView";
import { Button } from "src/components/common/FormComponents/Buttons";
import SearchBar from "src/components/common/SearchField";
import { fundStatus } from "src/constants/common";
import { path } from "src/constants/path";
import { useAppSelector } from "src/redux/store";
import {
  getArchivedFundRequests,
  recoverFundRequest,
} from "src/utils/api/dashboard";
import { dateAndTimeStamp, formatINR } from "src/utils/helper";

const ArchivedFundApplications = () => {
  const queryClient = useQueryClient();

  const user = useAppSelector((state) => state.root.user);
  const canRecover = user?.data?.role !== "Approver-view-only";

  const [tableRows, setTableRows] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recoverRow, setRecoverRow] = useState<any>(null);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 50,
    page: 0,
  });

  const [queryParams, setQueryParams] = useState<{
    offset: number;
    limit: number;
    searchText: string;
  }>({
    offset: 0,
    limit: 50,
    searchText: "",
  });

  const { isLoading, isFetching } = useQuery(
    ["archivedFundRequests", queryParams],
    () => getArchivedFundRequests(queryParams),
    {
      onSuccess: (res: AxiosResponse) => {
        const rows = (res?.data?.rows ?? []).map((row: any) => ({
          sno: row?.serialNo,
          id: row.id,
          rawStatus: row.status,
          faYear: row?.yearOfFundApplication,
          costEstimateID: row.landOwnershipEpid,
          registrationNo: row?.landOwnership?.projectDetail?.registrationNo,
          entityName: row?.landOwnership?.user?.firstName,
          state: row?.landOwnership?.projectDetail?.address?.stateDetail?.name,
          division:
            row?.landOwnership?.projectDetail?.landSpecification?.division,
          allocateDivision:
            row?.landOwnership?.projectDetail?.organizationAffiliationDetail
              ?.userDetail?.division,
          totalArea: `${row?.landOwnership?.netPlantationArea}`,
          ecoRestorationAmount: formatINR(
            row?.landOwnership?.demandNote ?? "-"
          ),
          estimatedAdvanceWorkCost: formatINR(row.estimatedAdvanceWorkCost),
          estimatedRaisingCost: formatINR(row.estimatedRaisingCost),
          estimatedMaintenanceCost: formatINR(row.estimatedMaintenanceCost),
          estimatedMonitoringCost: formatINR(row.estimatedMonitoringCost),
          estimatedContingencyCost: formatINR(row.estimatedContingencyCost),
          totalEstimatedCost: formatINR(row.totalEstimatedCost),
          advanceWorkCost: formatINR(row.advanceWorkCost),
          raisingCost: formatINR(row.raisingCost),
          maintenanceCost: formatINR(row.maintenanceCost),
          monitoringCost: formatINR(row.monitoringCost),
          contingencyCost: formatINR(row.contingencyCost),
          totalCost: formatINR(row.totalCost),
          totalAmountReleased: formatINR(row.totalAmountReleased),
          totalBalance: formatINR(
            Number(row?.landOwnership?.demandNote ?? 0) -
              Number(row?.cumulativeAmountReleased ?? 0)
          ),
          status: fundStatus[row.status] ?? row.status,
          created_at: dateAndTimeStamp(row.created_at, "IST"),
          updated_at: dateAndTimeStamp(row.updated_at, "IST"),
          archivedAt: row?.archivedAt
            ? dateAndTimeStamp(row.archivedAt, "IST")
            : "-",
        }));
        setTableRows(rows);
        setTotalRecords(res?.data?.count ?? 0);
      },
      onError: (_err: AxiosError) => {},
      refetchOnMount: true,
    }
  );

  const { mutate: recoverFa, isLoading: isRecovering } = useMutation(
    (id: string) => recoverFundRequest(id),
    {
      onSuccess: () => {
        setRecoverRow(null);
        queryClient.invalidateQueries(["archivedFundRequests"]);
        // A restore re-numbers the parcel's FA years, so the main Fund
        // Application list is stale for more than just the returning row.
        queryClient.invalidateQueries(["users"]);
      },
    }
  );

  const handlePaginationChange = (newModel: any) => {
    setPaginationModel(newModel);
    setQueryParams((prev) => ({
      ...prev,
      offset: newModel.page * newModel.pageSize,
      limit: newModel.pageSize,
    }));
  };

  const RenderFaId = (params: any) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Box
        component={RouterLink}
        to={`${path.fundApplicationDetails}?${createSearchParams({
          id: params?.row?.id,
        })}`}
        sx={{
          color: "#16A085",
          fontWeight: 600,
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {params?.row?.id}
      </Box>
      {!!params?.row?.faYear && (
        <Box
          component="span"
          sx={{
            color: "#8E44AD",
            backgroundColor: "rgba(142, 68, 173, 0.12)",
            fontWeight: 700,
            fontSize: "11px",
            padding: "3px 6px",
            borderRadius: "4px",
            fontStyle: "italic",
          }}
        >
          {`FA${params.row.faYear}`}
        </Box>
      )}
    </Box>
  );

  const RenderEPID = (params: any) => (
    <Box
      component="a"
      href={`${path.landOwnershipDetails}?${createSearchParams({
        id: params?.row?.costEstimateID,
      })}`}
      sx={{ color: "#16A085", fontWeight: 500 }}
    >
      {params?.row?.costEstimateID}
    </Box>
  );

  const tableColumns: any = [
    { field: "sno", headerName: "S.No", width: 90, sortable: false },
    {
      field: "id",
      headerName: "FA-ID",
      width: 230,
      sortable: false,
      renderCell: RenderFaId,
    },
    {
      field: "costEstimateID",
      headerName: "Cost Estimate ID",
      width: 220,
      sortable: false,
      renderCell: RenderEPID,
    },
    {
      field: "registrationNo",
      headerName: "Reg.No",
      width: 100,
      sortable: false,
    },
    {
      field: "entityName",
      headerName: "Entity Name",
      width: 150,
      sortable: false,
    },
    { field: "state", headerName: "State", width: 150, sortable: false },
    { field: "division", headerName: "Division", width: 150, sortable: false },
    {
      field: "allocateDivision",
      headerName: "Allocate Forest Division",
      width: 200,
      sortable: false,
    },
    {
      field: "totalArea",
      headerName: "Net Planned Area(Ha)",
      width: 150,
      sortable: false,
    },
    {
      field: "ecoRestorationAmount",
      headerName: "Eco-Restoration Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "estimatedAdvanceWorkCost",
      headerName: "Est.Advance Work Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "estimatedRaisingCost",
      headerName: "Est.Raising Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "estimatedMaintenanceCost",
      headerName: "Est.Maintenance Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "estimatedMonitoringCost",
      headerName: "Est.Monitoring Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "estimatedContingencyCost",
      headerName: "Est.Contingency Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "totalEstimatedCost",
      headerName: "Est.Total Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "advanceWorkCost",
      headerName: "Advance Work Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "raisingCost",
      headerName: "Raising Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "maintenanceCost",
      headerName: "Maintenance Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "monitoringCost",
      headerName: "Monitoring Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "contingencyCost",
      headerName: "Contingency Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "totalCost",
      headerName: "Total Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "totalAmountReleased",
      headerName: "Total Amount Released(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "totalBalance",
      headerName: "Total Balance(₹)",
      width: 200,
      sortable: false,
    },
    { field: "status", headerName: "Status", width: 220, sortable: false },
    {
      field: "created_at",
      headerName: "Created At",
      width: 150,
      sortable: false,
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      width: 150,
      sortable: false,
    },
    {
      field: "archivedAt",
      headerName: "Archived At",
      width: 180,
      sortable: false,
    },
    ...(canRecover
      ? [
          {
            field: "actions",
            headerName: "Recover",
            width: 110,
            sortable: false,
            renderCell: (params: any) => (
              <Tooltip title="Recover">
                <IconButton
                  size="small"
                  onClick={() => setRecoverRow(params.row)}
                  sx={{ color: "#16A085" }}
                >
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  return (
    <DashboardWrapper title="Archived Fund Applications" showBackBtn>
      <Box>
        <Box
          sx={{
            padding: "10px 1%",
            background: "white",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
          }}
        >
          <SearchBar
            getText={(e) =>
              setQueryParams((prev) => ({
                ...prev,
                offset: 0,
                searchText: `${e}`,
              }))
            }
            customStyles={{ wrapper: { width: "80%" } }}
          />
        </Box>

        {isLoading || isFetching ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", padding: "40px" }}
          >
            <CircularProgress />
          </Box>
        ) : tableRows.length > 0 ? (
          <DataGrid
            rows={tableRows}
            columns={tableColumns}
            rowCount={totalRecords}
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            onPageChange={(newPage: number) =>
              handlePaginationChange({ ...paginationModel, page: newPage })
            }
            onPageSizeChange={(newPageSize: number) =>
              handlePaginationChange({
                ...paginationModel,
                pageSize: newPageSize,
              })
            }
            rowsPerPageOptions={[50]}
            paginationMode="server"
            columnBuffer={20}
            loading={isLoading || isFetching}
            getRowId={(row: any) => row.id}
          />
        ) : (
          <EmptyView
            heading="No Archived Fund Applications"
            subheading=""
            onClick={() => {}}
          />
        )}
      </Box>

      <Dialog
        open={!!recoverRow}
        onClose={() => {
          if (!isRecovering) setRecoverRow(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Recover fund application
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will restore fund application <strong>{recoverRow?.id}</strong>{" "}
            back to the active list.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            text="Cancel"
            onClick={() => setRecoverRow(null)}
            disabled={isRecovering}
          />
          <Button
            text={isRecovering ? "Recovering..." : "Recover"}
            onClick={() => recoverFa(recoverRow?.id)}
            disabled={isRecovering}
          />
        </DialogActions>
      </Dialog>
    </DashboardWrapper>
  );
};

export default ArchivedFundApplications;
